import axiosInstance, { ApiKeys, getApiUrl } from '@cp/src/utils/ApiHelper';
import { toast } from '@navi/web-ui/lib/primitives/Toast';
import { Dispatch } from '@reduxjs/toolkit';
import {
  setRecommendedCases,
  setRecommendedMetadata,
  appendRecommendedCases,
  setRecommendedCasesLoading
} from './reducer';
import { params, RecommendedCaseData } from './types';
import { API_STATUS_CODE } from '@cp/src/utils/ApiHelperUtils';

export const fetchCaseRecommendationMetadata = () => (dispatch: Dispatch) => {
  const url = getApiUrl(ApiKeys.CASE_RECOMMENDATION_STATS);

  axiosInstance
    .get(url, {
      headers: {
        donotHandleError: true,
        includeInUptimeMetrics: true
      }
    })
    .then(response => {
      if (response.data) {
        dispatch(setRecommendedMetadata(response.data));
      }
    })
    .catch(error => {
      toast.error('Error fetching case recommendation metadata');
    });
};

export const fetchCaseRecommendationCurrentCase =
  (successCallback: (currentRecommendedCase: RecommendedCaseData) => void) =>
  (dispatch: Dispatch) => {
    const url = getApiUrl(ApiKeys.CASE_RECOMMENDATION_CURRENT_CASE);

    axiosInstance
      .get(url, {
        headers: {
          donotHandleError: true,
          includeInUptimeMetrics: true
        }
      })
      .then(response => {
        if (response.data) {
          successCallback(response.data);
        }
      })
      .catch(error => {
        toast.error('Error fetching case recommendation current case');
      });
  };

export const fetchCaseRecommendationNextCase =
  (successCallback: (currentRecommendedCase: RecommendedCaseData) => void) =>
  (dispatch: Dispatch) => {
    const url = getApiUrl(ApiKeys.CASE_RECOMMENDATION_NEXT_CASE);

    axiosInstance
      .post(
        url,
        {},
        {
          headers: {
            donotHandleError: true,
            includeInUptimeMetrics: true
          }
        }
      )
      .then(response => {
        if (response.data) {
          successCallback(response.data);
        }
      })
      .catch(error => {
        toast.error('Error fetching case recommendation next case');
      });
  };

export const fetchCaseRecommendationCases =
  ({ limit, offset }: params) =>
  (dispatch: Dispatch) => {
    const url = getApiUrl(ApiKeys.CASE_RECOMMENDATION_CASES);

    dispatch(setRecommendedCasesLoading(true));
    axiosInstance
      .get(url, {
        params: { limit, offset },
        headers: { donotHandleError: true, includeInUptimeMetrics: true }
      })
      .then(response => {
        if (response.data) {
          dispatch(setRecommendedCases(response.data));
        }
      })
      .catch(error => {
        toast.error('Error fetching case recommendation cases');
      })
      .finally(() => {
        dispatch(setRecommendedCasesLoading(false));
      });
  };

export const fetchMoreCaseRecommendationCases =
  ({ limit, offset }: params) =>
  (dispatch: Dispatch) => {
    const url = getApiUrl(ApiKeys.CASE_RECOMMENDATION_CASES);

    axiosInstance
      .get(url, {
        params: { limit, offset },
        headers: { donotHandleError: true, includeInUptimeMetrics: true }
      })
      .then(response => {
        if (response.data) {
          dispatch(appendRecommendedCases(response.data));
        }
      })
      .catch(error => {
        toast.error('Error fetching more case recommendation cases');
      });
  };

export const fetchCaseRecommendationPreviousCase =
  (successCallback: (currentRecommendedCase: RecommendedCaseData) => void) =>
  (dispatch: Dispatch) => {
    const url = getApiUrl(ApiKeys.CASE_RECOMMENDATION_PREVIOUS_CASE);

    axiosInstance
      .post(
        url,
        {},
        {
          headers: {
            donotHandleError: true,
            includeInUptimeMetrics: true
          }
        }
      )
      .then(response => {
        if (response.data) {
          successCallback(response.data);
        }
      })
      .catch(error => {
        toast.error('Error fetching case recommendation previous case');
      });
  };

export const fetchRefreshCaseRecommendation =
  ({ limit, offset, successCallback }: params) =>
  (dispatch: Dispatch) => {
    const url = getApiUrl(ApiKeys.CASE_RECOMMENDATION_REFRESH);

    dispatch(setRecommendedCasesLoading(true));
    axiosInstance
      .post(
        url,
        {
          params: { limit, offset }
        },
        {
          headers: { donotHandleError: true, includeInUptimeMetrics: true }
        }
      )
      .then(response => {
        if (response.status === API_STATUS_CODE.OK) {
          successCallback?.();
        }
      })
      .catch(error => {
        toast.error('Error refreshing case recommendation');
      })
      .finally(() => {
        dispatch(setRecommendedCasesLoading(false));
      });
  };

export const fetchLatestCurrentCase =
  (successCallback: (currentRecommendedCase: RecommendedCaseData) => void) =>
  (dispatch: Dispatch) => {
    const url = getApiUrl(ApiKeys.CASE_RECOMMENDATION_LATEST_CURRENT_CASE);

    axiosInstance
      .get(url, {
        headers: {
          donotHandleError: true
        }
      })
      .then(response => {
        if (response.data) {
          successCallback(response.data);
        }
      })
      .catch(error => {
        toast.error('Error fetching case recommendation current case');
      });
  };
